import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { type ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const ReactSelectRoles = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles', networkId, inputValue],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get(Routes.backoffice_network_roles_path(networkId), {
          params: {
            fulltext: inputValue,
            format: 'json',
            per_page: 25,
          },
        })
        .then((res) => res.data),
    staleTime: ms('5m'),
  });

  const onInputChange = useDebouncedCallback((value) => {
    setInputValue(value);
  }, 500);

  const options = useMemo(() => {
    const combinedRoles = [
      ...(roles || []),
      ...(Array.isArray(props.value) ? props.value : [props.value]),
    ];
    const uniqueRolesMap = new Map();

    combinedRoles.forEach((role) => {
      if (role && typeof role.id !== 'undefined') {
        uniqueRolesMap.set(role.id, role);
      }
    });

    return Array.from(uniqueRolesMap.values()).map(({ id, name }) => ({
      value: id,
      label: name,
    }));
  }, [roles, props.value]);

  return (
    <ReactSelectInput
      placeholder={__('Search and select roles')}
      isMulti
      {...props}
      cacheOptions
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectRoles;
